import { AISuggestionsMatcherVariant } from '@/modules/aiSuggestions/AISuggestionsMatcher/AISuggestionsMatcher.types';
import { CommonAiSuggestionFragment } from '@/modules/aiSuggestions/graphql/aiSuggestions.generated';
import {
  AiSuggestionKind,
  AugmentedCreateTestamentaryEntityInput,
  AugmentedUpdateTestamentaryEntityInput,
  EntityGstStatus,
} from '@/types/schema';

import {
  BENEFICIARIES_AFTER_BOTH_GRANTORS_DIE,
  BENEFICIARIES_SUBFORM,
} from '../../EntityBeneficiariesSubform/EntityBeneficiariesSubform.constants';
import { EntityBeneficiariesFormBeneficiary } from '../../EntityBeneficiariesSubform/EntityBeneficiariesSubform.types';
import { makeCreateBeneficiaryInputs } from '../../EntitySubforms/utils/shared/beneficiaries.utils';
import { makeCreateControllingPartyInputs } from '../../EntitySubforms/utils/shared/controllingParties.utils';
import {
  entityGstStatusToApiTypeMap,
  estateInclusionStatusToApiTypeMap,
} from '../../EntitySubforms/utils/shared/taxStatus.utils';
import {
  makeCreateSuccessorTrusteeInputs,
  makeCreateTrustAdvisorInputs,
  makeCreateTrusteeInputs,
} from '../../EntitySubforms/utils/shared/trustees.utils';
import { MIXED_GST } from '../../TaxStatusSubform/TaxStatusSubform.constants';
import { TRUST_DETAILS_SUBFORM_NAMESPACE } from '../../TrustDetailsSubform/TrustDetailsSubform.types';
import {
  CONTROLLING_PARTY_SUBFORM,
  TESTAMENTARY_ENTITY,
} from './EditTestamentaryEntitySplitScreen.constants';
import {
  EditTestamentaryEntityFormShape,
  EditTestamentaryEntitySection,
} from './EditTestamentaryEntitySplitScreen.types';

function getCreateTestamentaryEntityInput(
  form: EditTestamentaryEntityFormShape,
  householdId: string
): AugmentedCreateTestamentaryEntityInput {
  const testamentaryEntityForm = form[TESTAMENTARY_ENTITY];
  const trustDetailsForm = form[TRUST_DETAILS_SUBFORM_NAMESPACE];
  const controllingPartiesSubform = form[CONTROLLING_PARTY_SUBFORM];
  const beneficiariesSubform = form[BENEFICIARIES_SUBFORM];
  const beneficiariesAfterBothGrantorDeathsSubform =
    form[BENEFICIARIES_AFTER_BOTH_GRANTORS_DIE];

  if (
    !testamentaryEntityForm.displayName ||
    !testamentaryEntityForm.testamentaryEntityKind
  ) {
    throw new Error('missing required field for testamentary entity');
  }

  const {
    displayName,
    testamentaryEntityKind,
    estateInclusionStatus,
    gstStatus,
    inclusionRatio,
    entityDetailsNotes,
    survivingSpouseStateInEstateStatus,
    accountOwnerId,
  } = testamentaryEntityForm;

  if (!householdId) {
    throw new Error('a household ID is required');
  }

  const beneficiaries: EntityBeneficiariesFormBeneficiary[] =
    beneficiariesSubform.beneficiaries ?? [];
  const beneficiariesAfterBothDie: EntityBeneficiariesFormBeneficiary[] =
    beneficiariesAfterBothGrantorDeathsSubform?.beneficiaries ?? [];
  const withBeneficiaries = makeCreateBeneficiaryInputs(
    beneficiaries,
    householdId
  );
  const withBeneficiariesAfterBothGrantorDeaths =
    beneficiariesAfterBothDie.length > 0
      ? {
          withBeneficiariesAfterBothGrantorDeaths: makeCreateBeneficiaryInputs(
            beneficiariesAfterBothDie,
            householdId
          ),
        }
      : {};

  const output: AugmentedCreateTestamentaryEntityInput = {
    create: {
      displayName,
      kind: testamentaryEntityKind,
      inEstateStatus: estateInclusionStatus
        ? estateInclusionStatusToApiTypeMap[estateInclusionStatus]
        : undefined,
      gstStatus: gstStatus ? entityGstStatusToApiTypeMap[gstStatus] : undefined,
      inclusionRatio:
        gstStatus === MIXED_GST && inclusionRatio ? inclusionRatio : undefined,
      description: entityDetailsNotes || undefined,
      householdID: householdId,
      survivingSpouseStateInEstateStatus: survivingSpouseStateInEstateStatus
        ? estateInclusionStatusToApiTypeMap[survivingSpouseStateInEstateStatus]
        : undefined,
      accountOwnerID: accountOwnerId || undefined,
    },
    withTrustees: makeCreateTrusteeInputs(trustDetailsForm, householdId),
    withSuccessorTrustees: makeCreateSuccessorTrusteeInputs(
      trustDetailsForm,
      householdId
    ),
    withTrustAdvisors: makeCreateTrustAdvisorInputs(
      trustDetailsForm,
      householdId
    ),
    withDispositiveProvisions: [],
    withControllingParties: controllingPartiesSubform
      ? makeCreateControllingPartyInputs(controllingPartiesSubform, householdId)
      : undefined,
    withBeneficiaries,
    ...withBeneficiariesAfterBothGrantorDeaths,
  };

  return output;
}

interface UpdateTestamentaryEntityInputArg {
  formValues: EditTestamentaryEntityFormShape;
  testamentaryEntityId: string;
  householdId: string;
}

export function getUpdateTestamentaryEntityInput({
  formValues,
  testamentaryEntityId,
  householdId,
}: UpdateTestamentaryEntityInputArg): AugmentedUpdateTestamentaryEntityInput {
  // Get the form values as if we were creating
  const createInput = getCreateTestamentaryEntityInput(formValues, householdId);
  const {
    gstStatus,
    inclusionRatio,
    inEstateStatus,
    survivingSpouseStateInEstateStatus,
    displayName,
    taxStatus,
    description,
    kind,
    accountOwnerID,
  } = createInput.create;

  const {
    withTrustees,
    withSuccessorTrustees,
    withTrustAdvisors,
    withControllingParties,
    withBeneficiaries,
    withBeneficiariesAfterBothGrantorDeaths,
  } = createInput;

  const createdFromEntityIds =
    formValues[TESTAMENTARY_ENTITY].createdFromEntityIds ?? [];

  return {
    id: testamentaryEntityId,
    update: {
      clearTrustees: true,
      clearSuccessorTrustees: true,
      clearTrustAdvisors: true,
      clearControllingParties: true,
      clearDescription: !description,
      clearBeneficiaries: true,
      clearBeneficiariesAfterBothGrantorDeaths: true,
      clearGstStatus: !gstStatus,
      clearInclusionRatio:
        !inclusionRatio || gstStatus !== EntityGstStatus.MixedGst,
      clearCreatedFromEntities: true,
      gstStatus,
      inclusionRatio:
        gstStatus === EntityGstStatus.MixedGst && inclusionRatio
          ? inclusionRatio
          : undefined,
      inEstateStatus,
      taxStatus,
      displayName,
      survivingSpouseStateInEstateStatus,
      description: description || undefined,
      kind,
      accountOwnerID,
      clearAccountOwner: !accountOwnerID,
      addCreatedFromEntityIDs:
        createdFromEntityIds.length > 0 ? createdFromEntityIds : undefined,
    },
    withTrustees,
    withSuccessorTrustees,
    withTrustAdvisors,
    withControllingParties,
    withBeneficiaries,
    withBeneficiariesAfterBothGrantorDeaths,
  };
}

// ============================================================================
// AI Suggestions Utilities
// ============================================================================

/**
 * Maps a testamentary entity section to the AI suggestions matcher variant.
 * Returns undefined for sections that don't have AI suggestions.
 */
export function getMatcherVariantForEditTestamentaryEntitySection(
  section: EditTestamentaryEntitySection
): AISuggestionsMatcherVariant | undefined {
  switch (section) {
    case EditTestamentaryEntitySection.TRUSTEES:
      return AISuggestionsMatcherVariant.SUBTRUST_TRUSTEES;
    case EditTestamentaryEntitySection.ACCESS_AFTER_DEATH:
      return AISuggestionsMatcherVariant.SUBTRUST_LIFETIME_BENEFICIARIES;
    case EditTestamentaryEntitySection.ACCESS_AFTER_BOTH_DEATHS:
      return AISuggestionsMatcherVariant.SUBTRUST_REMAINDER_BENEFICIARIES;
    default:
      return undefined;
  }
}

/**
 * Maps a testamentary entity section to the relevant AI suggestion kinds.
 */
export function getAISuggestionKindsForTestamentaryEntitySection(
  section: EditTestamentaryEntitySection
): AiSuggestionKind[] {
  switch (section) {
    case EditTestamentaryEntitySection.BASIC_INFORMATION:
      return [
        AiSuggestionKind.SubtrustGstStatus,
        AiSuggestionKind.SubtrustCreatedFrom,
      ];
    case EditTestamentaryEntitySection.TRUSTEES:
      return [
        AiSuggestionKind.SubtrustTrustee,
        AiSuggestionKind.SubtrustSuccessorTrustee,
        AiSuggestionKind.SubtrustTrustAdvisor,
      ];
    case EditTestamentaryEntitySection.ACCESS_AFTER_DEATH:
      return [AiSuggestionKind.SubtrustLifetimeBeneficiary];
    case EditTestamentaryEntitySection.ACCESS_AFTER_BOTH_DEATHS:
      return [AiSuggestionKind.SubtrustRemainderBeneficiary];
    default:
      return [];
  }
}

/**
 * Gets AI suggestions for a specific testamentary entity section.
 */
export function getAISuggestionsForTestamentaryEntitySection(
  currentSection: EditTestamentaryEntitySection,
  suggestionsByKind: Record<AiSuggestionKind, CommonAiSuggestionFragment[]>
): CommonAiSuggestionFragment[] {
  const kinds =
    getAISuggestionKindsForTestamentaryEntitySection(currentSection);
  return kinds.flatMap((kind) => suggestionsByKind[kind] ?? []);
}
